// Wiring Universal Links / App Links into the Pending Action pipeline
// (#1693, spec.md §6.1-6.2). Covers all three required states: cold start
// (getLaunchUrl), backgrounded (appUrlOpen while suspended, resumed by the
// OS), and already-open (appUrlOpen while running).
//
// @capacitor/app is in package.json (#1725); the native plugin registers only
// after `cap sync`. This reads window.Capacitor?.Plugins?.App directly rather
// than importing '@capacitor/app', the same defensive pattern
// src/services/platform/runtime.js uses for window.Capacitor itself, so on
// the web — or a device build synced before the plugin — it is a no-op.

import { normalizeDeepLink } from './normalizeDeepLink'
import { storePendingAction } from './pendingActionStorage'
import { isNativePlatform } from '../platform/runtime'

function getAppPlugin() {
  try {
    return (typeof window !== 'undefined' && window.Capacitor?.Plugins?.App) || null
  } catch {
    return null
  }
}

async function handleIncomingUrl(rawUrl, {hasSession, onResolvedAction}) {
  const action = normalizeDeepLink(rawUrl)
  if (!action) return

  if (hasSession()) {
    onResolvedAction(action)
    return
  }

  await storePendingAction(action)
}

/**
 * Registers the appUrlOpen listener and handles a cold-start launch URL.
 *
 * `hasSession()`/`onResolvedAction(action)` are supplied by the caller
 * (App.js) so this module carries no direct dependency on auth state or the
 * router — an already-signed-in user tapping a link is navigated
 * immediately via `onResolvedAction`; a signed-out one gets the action
 * stored, to be picked up by resumePendingAction.js after login.
 *
 * @return {function(): void} cleanup — removes the listener. A no-op on web
 *   or when the plugin isn't present (nothing was registered).
 */
export function registerNativeLinkListener({hasSession, onResolvedAction}) {
  if (!isNativePlatform()) return () => {}

  const App = getAppPlugin()
  if (!App) return () => {}

  let removed = false
  let handle = null

  App.addListener('appUrlOpen', ({url}) => {
    handleIncomingUrl(url, {hasSession, onResolvedAction})
  }).then(h => {
    if (removed) {
      h.remove()
    } else {
      handle = h
    }
  }).catch(() => {})

  if (typeof App.getLaunchUrl === 'function') {
    App.getLaunchUrl()
      .then(result => {
        if (result?.url) {
          handleIncomingUrl(result.url, {hasSession, onResolvedAction})
        }
      })
      .catch(() => {})
  }

  return () => {
    removed = true
    if (handle) {
      handle.remove()
      handle = null
    }
  }
}
